import { state, getServerName } from '../state';
import { getVoiceParticipants, getActiveVoiceSession, getOverlayState, overlayToggleMute, overlayToggleDeafen, overlayToggleCamera, overlayToggleScreen, overlayDisconnect, joinVoiceDirect, subscribeVoiceLevel, applyInputDevice, applyOutputDevice } from '../channels/voice';
import { iconHash, iconFileText, iconCheckSquare, iconList, iconActivity, iconSettings, iconChevronDown, iconKey, iconPlus, iconBell, iconUserPlus, iconLogOut, iconBriefcase, iconCopy, iconEdit, iconCheck, iconX, iconSearch } from '../icons';
import { showContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { showModal, closeModal, modalFrame, confirmModal, promptModal } from '../components/modal';
import { openUserSettings } from '../components/user-settings';
import { post, get } from '../api';
import { showToast, errToast } from '../components/toast';
import { isNamedGuestCategory, openNamedGuestCategorySettings } from '../components/named-guest-controls';
import type { Channel, ManagementChannel, ServerChannel, VoiceChannel } from '../types';

type SwitchChannelFn = (ch: Channel) => void;
type ServerActionFn = (serverId: string) => void;
type VoidFn = () => void;

export interface ChannelListCallbacks {
  onSwitchChannel: SwitchChannelFn;
  onServerSettings?: ServerActionFn;
  onManageMembers?: ServerActionFn;
  onManageRoles?: SwitchChannelFn;
  onLeaveServer?: ServerActionFn;
  onInvitePeople?: VoidFn;
  onChannelsChanged?: VoidFn;
}

interface ManagementDef {
  name: ManagementChannel;
  label: string;
  icon: () => string;
}

const iconSpeaker = (): string => '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';

// Roles + rules live in Server Settings (gear icon / context menu), not as
// channel-list entries — Discord 1:1. The remaining four are gated per-server
// by `servers.management_enabled` (migration 056) so non-business servers
// (personal / family / community / gaming) don't see business-app chrome.
// `logs` and `settings` are universally useful, so they stay outside the gate.
const MANAGEMENT_GATED: ManagementDef[] = [
  { name: 'contractor', label: 'contractor', icon: iconBriefcase },
  { name: 'agreements', label: 'agreements', icon: iconFileText },
  { name: 'approvals',  label: 'approvals',  icon: iconCheckSquare },
  { name: 'directory',  label: 'directory',  icon: iconList },
];

const MANAGEMENT_ALWAYS: ManagementDef[] = [
  { name: 'logs',       label: 'logs',       icon: iconActivity },
  { name: 'settings',   label: 'settings',   icon: iconSettings },
];

/** Track which categories are collapsed (per-server, persisted). */
const collapsedCategories = new Set<string>();
let collapsedForServer = '';

function collapsedStorageKey(serverId: string): string {
  return `hub-people-collapsed-cats:${serverId}`;
}

function loadCollapsed(serverId: string): void {
  if (collapsedForServer === serverId) return;
  collapsedForServer = serverId;
  collapsedCategories.clear();
  try {
    const raw = localStorage.getItem(collapsedStorageKey(serverId));
    if (!raw) return;
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) {
      for (const k of arr) if (typeof k === 'string') collapsedCategories.add(k);
    }
  } catch { /* ignore parse / storage errors */ }
}

function persistCollapsed(serverId: string): void {
  try {
    localStorage.setItem(
      collapsedStorageKey(serverId),
      JSON.stringify([...collapsedCategories]),
    );
  } catch { /* ignore */ }
}

/** Channels with unread activity (managed externally) */
const unreadChannels = new Set<string>();
const pendingCreatedTextChannelIds = new Set<string>();
const pendingCreatedVoiceChannelIds = new Set<string>();

/** Active LiveKit-level subscription so we can cancel before re-attaching on
 *  the next render — prevents stacking listeners across voice join/leave
 *  cycles and across channel-list re-renders. */
let voiceMeterUnsub: (() => void) | null = null;

function attachVoiceMeter(): void {
  if (voiceMeterUnsub) {
    voiceMeterUnsub();
    voiceMeterUnsub = null;
  }
  const meter = document.getElementById('cl-call-meter');
  if (!meter) return;
  const segs = Array.from(meter.querySelectorAll<HTMLElement>('.cl-call-meter-seg'));
  if (segs.length === 0) return;
  // Loudest active speaker → number of bright segments. LiveKit audioLevel
  // is roughly 0..1 (RMS); Discord's bar reaches "full" before clipping so
  // we map 0.5+ to all 5 lit. ActiveSpeakersChanged fires ~20Hz which is
  // smooth enough without rAF (no leaks, no extra animation frame budget).
  voiceMeterUnsub = subscribeVoiceLevel((level) => {
    const lit = Math.max(0, Math.min(5, Math.round(level / 0.5 * 5)));
    for (let i = 0; i < segs.length; i++) {
      segs[i]?.classList.toggle('cl-call-meter-on', i < lit);
    }
  });
}

export function setChannelUnread(channel: string, unread: boolean): void {
  if (unread) unreadChannels.add(channel);
  else unreadChannels.delete(channel);
}

export function renderChannelList(container: HTMLElement, callbacks: ChannelListCallbacks): void {
  const serverId = state.currentServer;
  if (!serverId) {
    container.innerHTML = `<div class="cl-header">Overview</div><div class="cl-empty-msg">Select a server to see channels</div>`;
    return;
  }

  loadCollapsed(serverId);
  const serverName = getServerName(serverId);
  const current = state.currentChannel;

  const pendingApprovals = state.approvals.filter(a => a.server_id === serverId && a.status === 'pending').length;

  const memberCount = state.serverMembers.filter(m => m.server_id === serverId).length;
  const boostLevel = memberCount >= 10 ? 3 : memberCount >= 5 ? 2 : memberCount >= 2 ? 1 : 0;
  const boostBadge = boostLevel > 0 ? `<span class="cl-boost-badge" title="Level ${boostLevel}">&#10024; ${boostLevel}</span>` : '';

  let html = `<div class="cl-header cl-header-clickable" id="cl-header-dropdown">${escHtml(serverName)} ${boostBadge}<span class="cl-header-arrow">${iconChevronDown()}</span></div>`;

  // Discord-style search bar below the server header — clicking or focusing
  // this opens the Quick Switcher (same Ctrl+K component), since typing here
  // is a one-liner filter across all servers/channels/DMs rather than a
  // per-server filter.
  html += `<div class="cl-search-wrap">
    <button type="button" class="cl-search" id="cl-search-btn" aria-label="Open quick switcher">
      <span class="cl-search-icon">${iconSearch()}</span>
      <span class="cl-search-placeholder">Search</span>
      <span class="cl-search-kbd">Ctrl K</span>
    </button>
  </div>`;

  // Text channels come from /api/channels (server_channels table).
  // Voice channels come from /api/voice-channels (LiveKit-backed).
  const serverChannels = state.channels.filter(c => c.server_id === serverId);
  const allTextChannels = serverChannels.filter(c => c.channel_type === 'text' || c.channel_type === 'announcement' || c.channel_type === 'cc_chat');
  // Hide channels the current user can't read (null = not loaded yet, show all).
  let textChannels = state.visibleChannelNames === null
    ? allTextChannels
    : allTextChannels.filter(c => state.visibleChannelNames!.has(c.name));
  // Feature flag: hide devs-launch when dev_session_console is off.
  if (state.flags['dev_session_console'] === false) {
    textChannels = textChannels.filter(c => c.name !== 'devs-launch');
  }
  // Feature flag: hide #cc when cc_chat_channel is off.
  if (state.flags['cc_chat_channel'] === false) {
    textChannels = textChannels.filter(c => c.channel_type !== 'cc_chat');
  }
  const voiceChannels = state.voiceChannels.filter(c => c.server_id === serverId);

  // Wrap the scrollable channel area so the header, search, voice tray,
  // and user bar stay pinned while the channel/category list scrolls
  // independently \u2014 Discord parity. Without this wrapper, a server with
  // many channels pushes the user bar off-screen.
  html += `<div class="cl-channels" id="cl-channels">`;

  // Group text channels by category. Categories with "/" delimiters form a
  // parent/sub-category tree: "Notes/My Notes" has parent "Notes" and sub
  // "My Notes". Channels in "Notes/My Notes" render under the sub-category
  // indented under the parent. Parent categories with sub-categories become
  // board-view triggers (click opens a card grid overview of all subs).
  const byCategory = new Map<string, ServerChannel[]>();
  for (const ch of textChannels) {
    const cat = (ch.category && ch.category.trim()) || 'Text Channels';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(ch);
  }
  // Include user-created EMPTY categories
  for (const emptyCat of getEmptyCategories(serverId)) {
    if (!byCategory.has(emptyCat)) byCategory.set(emptyCat, []);
  }
  // Sort each category's channels by position, then name.
  for (const list of byCategory.values()) {
    list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name));
  }

  // Build the parent/sub tree. A category "Parent/Sub" is a child of "Parent".
  // Top-level categories (no "/") render normally. Parent categories that ONLY
  // have sub-categories (no direct channels of their own) become board-view
  // headers. Sub-categories render indented under their parent.
  interface CatNode { label: string; fullKey: string; channels: ServerChannel[]; subs: CatNode[] }
  const catTree: CatNode[] = [];
  const parentMap = new Map<string, CatNode>();

  // First pass: identify all unique category names
  const allCatNames = Array.from(byCategory.keys());

  // Sort categories
  allCatNames.sort((a, b) => {
    if (a === 'Text Channels' && b !== 'Text Channels') return -1;
    if (b === 'Text Channels' && a !== 'Text Channels') return 1;
    const aMin = Math.min(...(byCategory.get(a) || []).map(c => c.position ?? 0));
    const bMin = Math.min(...(byCategory.get(b) || []).map(c => c.position ?? 0));
    return aMin - bMin || a.localeCompare(b);
  });

  for (const cat of allCatNames) {
    const slashIdx = cat.indexOf('/');
    if (slashIdx > 0) {
      // Sub-category: "Parent/SubName"
      const parentName = cat.substring(0, slashIdx).trim();
      const subName = cat.substring(slashIdx + 1).trim();
      if (!subName) continue; // malformed
      let parent = parentMap.get(parentName);
      if (!parent) {
        parent = { label: parentName, fullKey: parentName, channels: byCategory.get(parentName) || [], subs: [] };
        parentMap.set(parentName, parent);
        catTree.push(parent);
      }
      parent.subs.push({ label: subName, fullKey: cat, channels: byCategory.get(cat) || [], subs: [] });
    } else if (!parentMap.has(cat)) {
      // Top-level category (not already created as a parent)
      const node: CatNode = { label: cat, fullKey: cat, channels: byCategory.get(cat) || [], subs: [] };
      parentMap.set(cat, node);
      catTree.push(node);
    }
  }

  // Render tree
  for (const node of catTree) {
    const catKey = `cat:${node.fullKey.toLowerCase().replace(/\s+/g, '-')}`;
    const hasSubs = node.subs.length > 0;

    if (hasSubs) {
      // Parent with sub-categories: render as a board-view parent header
      html += parentCategoryHeader(node.label, catKey, node.fullKey, serverId);
      const isCollapsed = collapsedCategories.has(catKey);
      if (!isCollapsed) {
        // If parent has direct channels, render them first
        if (node.channels.length > 0) {
          html += textChannelGroup(node.label, catKey + ':direct', node.channels, current, serverId, false);
        }
        // Render sub-categories indented
        for (const sub of node.subs) {
          const subKey = `cat:${sub.fullKey.toLowerCase().replace(/\s+/g, '-')}`;
          html += textChannelGroup(sub.label, subKey, sub.channels, current, serverId, true, sub.fullKey);
        }
      }
    } else {
      // Normal top-level category
      html += textChannelGroup(node.label, catKey, node.channels, current, serverId, false);
    }
  }

  html += voiceChannelGroup('Voice Channels', voiceChannels, current, serverId);
  const server = state.servers.find(s => s.id === serverId);
  const managementOn = (server?.management_enabled ?? 0) === 1;
  const managementChannels = managementOn
    ? [...MANAGEMENT_GATED, ...MANAGEMENT_ALWAYS]
    : MANAGEMENT_ALWAYS;
  html += managementGroup('Management', 'management', managementChannels, current, pendingApprovals);
  html += `</div>`;

  // Discord-style user panel at the bottom — clicking the avatar/name opens a
  // flyout panel UP with status picker, custom status, copy ID, edit profile,
  // and log out. Mic / deafen / settings stay inline so they don't require a
  // round-trip through the flyout.
  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
  const userName = owner?.name || (state.ownerId ? `Member-${state.ownerId.substring(0, 6)}` : 'Member');
  const userInitial = userName.charAt(0).toUpperCase();
  const userStatus = localStorage.getItem('hub-people-status') || 'online';
  const csTop = readCustomStatus();
  const customStatus = csTop.text;
  const customEmojiTop = csTop.emoji;

  // Persistent call overlay (A4) — Discord 1:1 layout. Header with green
  // dot + "Voice Connected" + voice-settings stub + disconnect; clickable
  // subtitle that jumps to the voice channel; 5-segment activity meter that
  // animates off the LiveKit ActiveSpeakersChanged stream.
  const activeVoice = getActiveVoiceSession();
  const ov = getOverlayState();
  if (activeVoice && ov) {
    const memberCount = activeVoice.participantCount;
    html += `<div class="cl-call-overlay" data-channel-id="${escAttr(activeVoice.channelId)}">
      <div class="cl-call-overlay-row">
        <div class="cl-call-overlay-status">
          <span class="cl-call-overlay-dot"></span>
          <span class="cl-call-overlay-label">Voice Connected</span>
        </div>
        <button class="cl-call-mini" id="cl-call-settings" type="button" title="Voice settings" aria-label="Voice settings">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h11"/><polyline points="13 6 19 12 13 18"/><path d="M19 4v16"/></svg>
        </button>
        <button class="cl-call-mini cl-call-mini-danger" id="cl-call-disconnect" type="button" title="Disconnect" aria-label="Disconnect">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>
        </button>
      </div>
      <button class="cl-call-overlay-channel" id="cl-call-open" type="button" title="Open ${escAttr(activeVoice.channelName)}">Custom [${memberCount}] / ${escHtml(activeVoice.channelName)}</button>
      <div class="cl-call-meter" id="cl-call-meter" aria-hidden="true">
        <span class="cl-call-meter-seg"></span>
        <span class="cl-call-meter-seg"></span>
        <span class="cl-call-meter-seg"></span>
        <span class="cl-call-meter-seg"></span>
        <span class="cl-call-meter-seg"></span>
      </div>
    </div>`;

    // A5 — quick voice action buttons row, only visible when in voice. Sits
    // between the call overlay and the user-bar so it disappears entirely
    // when not connected (no permanent gap).
    html += `<div class="cl-voice-quick">
      <button class="cl-vq-btn ${ov.cameraOn ? 'cl-vq-btn-active' : ''}" id="cl-vq-camera" title="${ov.cameraOn ? 'Turn off camera' : 'Turn on camera'}" aria-label="${ov.cameraOn ? 'Turn off camera' : 'Turn on camera'}" aria-pressed="${ov.cameraOn}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">${ov.cameraOn
          ? '<path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>'
          : '<path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>'}</svg>
      </button>
      <button class="cl-vq-btn ${ov.screenOn ? 'cl-vq-btn-active' : ''}" id="cl-vq-screen" title="${ov.screenOn ? 'Stop sharing' : 'Share your screen'}" aria-label="${ov.screenOn ? 'Stop sharing' : 'Share your screen'}" aria-pressed="${ov.screenOn}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.11-.9-2-2-2H4c-1.11 0-2 .89-2 2v10c0 1.1.89 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/></svg>
      </button>
      <button class="cl-vq-btn cl-vq-btn-disabled" id="cl-vq-soundboard" title="Soundboard (coming soon)" aria-label="Soundboard (coming soon)" disabled>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3a9 9 0 0 0-9 9 9 9 0 0 0 9 9 9 9 0 0 0 9-9 9 9 0 0 0-9-9zm-1 4h2v6h-2V7zm0 8h2v2h-2v-2z"/></svg>
      </button>
    </div>`;
  }

  // A1 — rich presence subtitle: custom-status > voice channel > current
  // text channel > status label. Voice + channel rows lead with an inline
  // SVG so the subtitle reads as Discord rich-presence rather than a plain
  // text status. Truncates via .cl-user-status-text ellipsis (~150px col).
  const subtitle = renderUserSubtitle(customStatus, customEmojiTop, activeVoice, current, userStatus);

  html += `<div class="cl-user-bar cl-user-panel" id="cl-user-panel" role="region" aria-label="User profile bar">
    <div class="cl-user-trigger" id="cl-account-trigger" title="Account, status, profile">
      <div class="cl-user-avatar-wrap">
        <div class="cl-user-avatar">${escHtml(userInitial)}</div>
        <div class="cl-user-status-dot cl-status-${escAttr(userStatus)}"></div>
      </div>
      <div class="cl-user-info">
        <div class="cl-user-name">${escHtml(userName)}</div>
        <div class="cl-user-status-text">${subtitle}</div>
      </div>
    </div>
    <div class="cl-user-controls">
      <div class="cl-ctrl-split" data-ctrl="mic">
        <button class="cl-ctrl-btn cl-ctrl-btn-main" id="cl-mic-btn" title="Mute" aria-label="Mute microphone">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
        </button>
        <button class="cl-ctrl-chevron" id="cl-mic-devices" title="Select microphone" aria-label="Select microphone">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
      </div>
      <div class="cl-ctrl-split" data-ctrl="deafen">
        <button class="cl-ctrl-btn cl-ctrl-btn-main" id="cl-deafen-btn" title="Deafen" aria-label="Deafen audio">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
        </button>
        <button class="cl-ctrl-chevron" id="cl-deafen-devices" title="Select audio output" aria-label="Select audio output">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
      </div>
      <button class="cl-ctrl-btn" id="cl-user-settings-btn" title="User Settings" aria-label="User Settings">${iconSettings()}</button>
    </div>
  </div>`;

  // Capture scroll position before replacing content, restore after (synchronously, before paint)
  const oldChannelsWrap = container.querySelector<HTMLElement>('#cl-channels');
  const savedScrollTop = oldChannelsWrap?.scrollTop ?? 0;

  container.innerHTML = html;

  // Restore scroll position immediately after DOM update, before any event handlers attach
  const newChannelsWrap = container.querySelector<HTMLElement>('#cl-channels');
  if (newChannelsWrap) {
    newChannelsWrap.scrollTop = savedScrollTop;
  }

  // Discord-parity hover actions on text channels: gear → channel settings,
  // user-plus → invite. Buttons live inside .cl-channel and stop propagation
  // so they don't trigger the row's switch-channel handler.
  container.querySelectorAll<HTMLButtonElement>('.cl-channel-action').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const action = btn.dataset['channelAction'] || '';
      const targetId = btn.dataset['channelTarget'] || '';
      const chName = btn.dataset['channelName'] || '';
      if (action === 'invite') {
        showInviteModal(serverId);
      } else if (action === 'settings') {
        void import('../components/channel-settings').then(({ openChannelSettings }) => {
          openChannelSettings(targetId, chName as Channel, 'general');
        });
      }
    });
  });

  // Channel click handlers
  container.querySelectorAll<HTMLElement>('.cl-channel').forEach(el => {
    el.addEventListener('click', () => {
      const ch = el.dataset['channel'] as Channel | undefined;
      if (!ch) return;
      if (typeof ch === 'string' && ch.startsWith('voice:')) {
        const vcId = ch.slice(6);
        const vc = state.voiceChannels.find(v => v.id === vcId);
        if (vc) void joinVoiceDirect(vc);
      }
      callbacks.onSwitchChannel(ch);
    });

    // Build context menu items for a channel row
    const buildChannelMenuItems = (ch: string, chId: string | undefined): ContextMenuItem[] => {
      const isOwner = !!state.ownerId && state.servers.some(s => s.id === serverId && s.owner_id === state.ownerId);
      // Lazy-import the notifications helpers so we don't widen the channel-list
      // bundle for the 99% of clicks that are channel-switches, not menus.
      const muted = (() => {
        try {
          const raw = localStorage.getItem('hub-people-muted-channels') || '[]';
          return (JSON.parse(raw) as string[]).includes(ch);
        } catch { return false; }
      })();
      const newTabIcon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
      const items: ContextMenuItem[] = [
        { label: 'Open in New Tab', icon: newTabIcon, action: () => {
          const url = `${window.location.origin}${window.location.pathname}#s/${serverId}/${encodeURIComponent(ch)}`;
          window.open(url, '_blank', 'noopener');
        }},
        { label: 'Mark as Read', icon: iconCheck(), action: () => { unreadChannels.delete(ch); renderChannelList(container, callbacks); } },
        { label: muted ? 'Unmute Channel' : 'Mute Channel', icon: iconBell(), action: () => {
          void import('../components/notifications').then(({ toggleChannelMute }) => {
            const nowMuted = toggleChannelMute(ch);
            showToast(nowMuted ? `#${ch} muted` : `#${ch} unmuted`, 'ok');
          });
        }},
        { label: 'Copy Channel ID', icon: iconCopy(), action: () => {
          void navigator.clipboard.writeText(chId || ch);
          showToast('Channel ID copied', 'ok');
        }},
        { label: 'Invite People', icon: iconUserPlus(), action: () => showInviteModal(serverId) },
        { label: 'Edit Channel', icon: iconEdit(), action: () => {
          void import('../components/channel-settings').then(({ openChannelSettings }) => {
            openChannelSettings(chId || '', ch, 'general');
          });
        }},
      ];
      if (isOwner) {
        const isVoice = ch.startsWith('voice:');
        const voiceId = isVoice ? ch.slice(6) : '';
        const displayName = isVoice ? (state.voiceChannels.find(v => v.id === voiceId)?.name || ch) : ch;
        items.push({
          label: isVoice ? 'Delete Voice Channel' : 'Delete Channel', icon: iconX(), danger: true,
          action: () => {
            confirmModal(isVoice ? 'Delete voice channel' : 'Delete channel', `Delete #${displayName}? This cannot be undone.`, () => {
              if (isVoice) {
                void post('/api/voice-channels/delete', { id: voiceId }).then(() => {
                  pendingCreatedVoiceChannelIds.delete(voiceId);
                  state.voiceChannels = state.voiceChannels.filter(v => v.id !== voiceId);
                  showToast(`#${displayName} deleted`, 'ok');
                  if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
                }).catch(err => errToast(err));
              } else {
                void post('/api/channels/delete', { id: chId }).then(() => {
                  if (chId) pendingCreatedTextChannelIds.delete(chId);
                  state.channels = state.channels.filter(c => c.id !== chId);
                  showToast(`#${displayName} deleted`, 'ok');
                  if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
                }).catch(err => errToast(err));
              }
            }, { confirmLabel: 'Delete', variant: 'danger' });
          },
        });
      }
      // Create channel / category — Discord puts these at the bottom of every
      // channel right-click menu so you don't have to find empty space.
      const chanCat = el.dataset['channelCat'] || 'Text Channels';
      items.push({ label: '', divider: true });
      items.push({ label: 'Create Channel', icon: iconPlus(), action: () => {
        showCreateChannelModal(serverId, callbacks, 'text', chanCat);
      }});
      items.push({ label: 'Create Category', icon: iconPlus(), action: () => {
        showCreateCategoryModal(serverId, callbacks);
      }});
      return items;
    };

    // Middle-click opens channel in new tab
    el.addEventListener('auxclick', (e) => {
      if (e.button !== 1) return;
      e.preventDefault();
      const ch = el.dataset['channel'];
      if (!ch) return;
      const url = `${window.location.origin}${window.location.pathname}#s/${serverId}/${encodeURIComponent(ch)}`;
      window.open(url, '_blank', 'noopener');
    });

    // Right-click context menu
    el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const ch = el.dataset['channel'];
      const chId = el.dataset['channelId'];
      if (!ch) return;
      showContextMenu(e.clientX, e.clientY, buildChannelMenuItems(ch, chId));
    });

    // Mobile: long-press 500ms triggers same menu
    let lpTimer: ReturnType<typeof setTimeout> | null = null;
    let lpStartY = 0;
    el.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      lpStartY = touch.clientY;
      const ch = el.dataset['channel'];
      const chId = el.dataset['channelId'];
      if (!ch) return;
      lpTimer = setTimeout(() => {
        lpTimer = null;
        try { navigator.vibrate(10); } catch { /* unsupported */ }
        showContextMenu(touch.clientX, touch.clientY, buildChannelMenuItems(ch, chId));
      }, 500);
    }, { passive: true });
    el.addEventListener('touchmove', (e) => {
      if (!lpTimer || !e.touches[0]) return;
      if (Math.abs(e.touches[0].clientY - lpStartY) > 10) {
        clearTimeout(lpTimer);
        lpTimer = null;
      }
    }, { passive: true });
    el.addEventListener('touchend', () => {
      if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; }
    }, { passive: true });
    el.addEventListener('touchcancel', () => {
      if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; }
    }, { passive: true });
  });

  // Per-category add-channel buttons. We render one + per category header so
  // Mitchell can drop a new channel directly into "AI" or "Dev Tools" without
  // having to move it post-create.
  container.querySelectorAll<HTMLButtonElement>('.cl-add-text-channel').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const cat = btn.dataset['category'] || 'Text Channels';
      showCreateChannelModal(serverId, callbacks, 'text', cat);
    });
  });
  container.querySelectorAll<HTMLButtonElement>('.cl-category-guest-settings').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const cat = btn.dataset['categoryFull'] || '';
      openNamedGuestCategorySettings(cat, () => {
        if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
      });
    });
  });
  // Board view button on parent categories — opens the board renderer
  container.querySelectorAll<HTMLButtonElement>('.cl-category-board-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const catFull = btn.dataset['categoryFull'] || '';
      if (!catFull) return;
      // Navigate to a board view for this parent category via the sentinel.
      callbacks.onSwitchChannel(`board:${catFull}` as Channel);
    });
  });

  const addVoiceBtn = container.querySelector<HTMLButtonElement>('#cl-add-voice-channel');
  addVoiceBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    showCreateChannelModal(serverId, callbacks, 'voice');
  });

  // Category toggle handlers + right-click context menu
  container.querySelectorAll<HTMLElement>('.cl-category').forEach(el => {
    el.addEventListener('click', (e) => {
      // Ignore clicks on the add button inside the header
      if ((e.target as HTMLElement).closest('.cl-category-add')) return;
      const cat = el.dataset['category'];
      if (!cat) return;
      if (collapsedCategories.has(cat)) collapsedCategories.delete(cat);
      else collapsedCategories.add(cat);
      persistCollapsed(serverId);
      renderChannelList(container, callbacks);
    });

    // Right-click → Discord-1:1 category menu. Note this is for ANY
    // collapsible header — text-channel categories, voice channels, and
    // management. The action set is filtered per-key (only user-created
    // text categories get rename/delete; built-in groups don't).
    el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const catKey = el.dataset['category'] || '';
      const catLabel = el.dataset['categoryLabel'] || catKey;
      const catFull = el.dataset['categoryFull'] || catLabel;
      const isCollapsed = collapsedCategories.has(catKey);
      const items: ContextMenuItem[] = [];

      // Mark all channels in this category as read
      items.push({ label: 'Mark As Read', icon: iconCheck(), action: () => {
        const chans = state.channels.filter(c => c.server_id === serverId
          && ((c.category && c.category === catFull) || (!c.category && catFull === 'Text Channels')));
        for (const c of chans) unreadChannels.delete(c.name);
        renderChannelList(container, callbacks);
      }});

      items.push({ label: isCollapsed ? 'Expand Category' : 'Collapse Category', icon: iconChevronDown(), action: () => {
        if (isCollapsed) collapsedCategories.delete(catKey);
        else collapsedCategories.add(catKey);
        persistCollapsed(serverId);
        renderChannelList(container, callbacks);
      }});

      // Create channel in this category — available for ALL categories
      items.push({ label: '', divider: true });
      if (isNamedGuestCategory(catFull)) {
        items.push({ label: `${catLabel} Guest Settings`, icon: iconSettings(), action: () => {
          openNamedGuestCategorySettings(catFull, () => {
            if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
          });
        }});
      }
      items.push({ label: `Create Channel`, icon: iconPlus(), action: () => {
        showCreateChannelModal(serverId, callbacks, 'text', catFull);
      }});
      items.push({ label: 'Create Sub-Category', icon: iconPlus(), action: () => {
        showCreateSubCategoryModal(serverId, catFull, callbacks);
      }});
      items.push({ label: 'Create Category', icon: iconPlus(), action: () => {
        showCreateCategoryModal(serverId, callbacks);
      }});

      // If this is a parent with subs, offer board view
      const hasSubs = el.dataset['hasSubs'] === 'true';
      const catFullName = el.dataset['categoryFull'] || catLabel;
      if (hasSubs) {
        items.push({ label: 'Open Board View', icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>', action: () => {
          callbacks.onSwitchChannel(`board:${catFullName}` as Channel);
        }});
      }

      // For non-default categories: rename / delete
      if (catFull !== 'Text Channels') {
        const parentPrefix = catFull.includes('/') ? catFull.substring(0, catFull.lastIndexOf('/') + 1) : '';
        items.push({ label: '', divider: true });
        items.push({ label: 'Edit Category', icon: iconEdit(), action: () => {
          promptModal(
            'Rename category',
            '',
            (newName) => {
              const trimmed = newName.trim();
              if (!trimmed || trimmed === catLabel) return;
              const newFullPath = parentPrefix + trimmed;
              const chans = state.channels.filter(c => c.server_id === serverId && c.category === catFull);
              Promise.all(chans.map(c => post('/api/channels/update', { id: c.id, category: newFullPath })))
                .then(() => {
                  const empty = getEmptyCategories(serverId).filter(x => x !== catFull);
                  if (chans.length === 0) empty.push(newFullPath);
                  localStorage.setItem(`hub-people-empty-cats:${serverId}`, JSON.stringify(empty));
                  if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
                  showToast(`Renamed to "${trimmed}"`, 'ok');
                })
                .catch(err => errToast(err));
            },
            { defaultValue: catLabel, placeholder: 'Category name', confirmLabel: 'Save', required: true }
          );
        }});
        items.push({ label: 'Delete Category', icon: iconX(), danger: true, action: () => {
          const chans = state.channels.filter(c => c.server_id === serverId && c.category === catFull);
          const removeEmpty = (): void => {
            const empty = getEmptyCategories(serverId).filter(x => x !== catFull);
            localStorage.setItem(`hub-people-empty-cats:${serverId}`, JSON.stringify(empty));
            if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
            showToast(`Category "${catLabel}" deleted`, 'ok');
          };
          if (chans.length > 0) {
            confirmModal(
              'Delete category',
              `"${catLabel}" still contains ${chans.length} channel(s). Move them to "Text Channels"?`,
              () => {
                Promise.all(chans.map(c => post('/api/channels/update', { id: c.id, category: 'Text Channels' })))
                  .then(removeEmpty)
                  .catch(err => errToast(err));
              },
              { confirmLabel: 'Move and delete', variant: 'danger' }
            );
          } else {
            removeEmpty();
          }
        }});
      }

      showContextMenu(e.clientX, e.clientY, items);
    });
  });

  // Right-click on the channel-list scrollable area — fires on the wrapper
  // itself OR on any gap/padding between children (empty space below all
  // categories). Channel rows and category headers have their own contextmenu
  // handlers that stopPropagation, so this only fires on unclaimed space.
  const channelsWrap = container.querySelector<HTMLElement>('#cl-channels');
  channelsWrap?.addEventListener('contextmenu', (e) => {
    // If a child handled it (channel row, category header), it already
    // called stopPropagation — this won't fire. So no target check needed.
    e.preventDefault();
    showContextMenu(e.clientX, e.clientY, [
      { label: 'Create Channel', icon: iconPlus(), action: () => showCreateChannelModal(serverId, callbacks, 'text') },
      { label: 'Create Category', icon: iconPlus(), action: () => showCreateCategoryModal(serverId, callbacks) },
      { label: '', divider: true },
      { label: 'Server Settings', icon: iconSettings(), action: () => callbacks.onServerSettings?.(serverId) },
      { label: 'Invite People', icon: iconUserPlus(), action: () => showInviteModal(serverId) },
    ]);
  });

  // Right-click on the server name header → same dropdown the click triggers.
  const headerCtxEl = container.querySelector<HTMLElement>('#cl-header-dropdown');
  headerCtxEl?.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    showServerDropdown(e.clientX, e.clientY, serverId, callbacks);
  });

  // User settings button in profile bar — open Discord-style overlay
  document.getElementById('cl-user-settings-btn')?.addEventListener('click', () => {
    openUserSettings();
  });

  // Account flyout — opens UP from the user bar with status, copy ID, edit profile, log out.
  document.getElementById('cl-account-trigger')?.addEventListener('click', (e) => {
    e.stopPropagation();
    showAccountFlyout(container, callbacks);
  });

  // Channel drag-reorder (Discord parity, behind hp_drag_reorder flag).
  // Drag a channel row onto another row in the same category to reorder.
  // Optimistically reorders state.channels and POSTs to dev2's
  // PUT /api/channels/reorder; failures fall through silently while dev2
  // still wires the endpoint up.
  if (state.flags['hp_drag_reorder']) {
    let chanDragId: string | null = null;
    container.querySelectorAll<HTMLElement>('.cl-channel[data-channel-id]').forEach(row => {
      row.addEventListener('dragstart', (e) => {
        chanDragId = row.dataset['channelId'] ?? null;
        if (e.dataTransfer && chanDragId) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', chanDragId);
        }
        row.classList.add('cl-channel-dragging');
      });
      row.addEventListener('dragend', () => {
        row.classList.remove('cl-channel-dragging');
        container.querySelectorAll('.cl-channel-drop').forEach(el => el.classList.remove('cl-channel-drop'));
      });
      row.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        row.classList.add('cl-channel-drop');
      });
      row.addEventListener('dragleave', () => row.classList.remove('cl-channel-drop'));
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        row.classList.remove('cl-channel-drop');
        const dstId = row.dataset['channelId'];
        const dstCat = row.dataset['channelCat'] || '';
        if (!chanDragId || !dstId || chanDragId === dstId) return;
        const all = state.channels.slice();
        const src = all.find(c => c.id === chanDragId);
        const dst = all.find(c => c.id === dstId);
        if (!src || !dst) return;
        // Move src right before dst in the array. If categories differ, also
        // reassign the source's category.
        if (src.category !== dst.category) src.category = dstCat;
        all.splice(all.indexOf(src), 1);
        all.splice(all.indexOf(dst), 0, src);
        state.channels = all;
        renderChannelList(container, callbacks);
        const order = all
          .filter(c => c.server_id === serverId)
          .map(c => ({ id: c.id, position: 0, category: c.category }));
        order.forEach((o, i) => { o.position = i; });
        void import('../api').then(({ put }) => {
          void put('/api/channels/reorder', { server_id: serverId, order })
            .catch(() => { /* dev2 endpoint not deployed yet */ });
        });
        chanDragId = null;
      });
    });
  }

  // Mic toggle with persist
  const micBtn = document.getElementById('cl-mic-btn');
  const deafenBtn = document.getElementById('cl-deafen-btn');
  if (localStorage.getItem('hub-people-muted') === 'true') micBtn?.classList.add('cl-ctrl-active');
  if (localStorage.getItem('hub-people-deafened') === 'true') deafenBtn?.classList.add('cl-ctrl-active');

  micBtn?.addEventListener('click', (e) => {
    const btn = e.currentTarget as HTMLElement;
    btn.classList.toggle('cl-ctrl-active');
    const muted = btn.classList.contains('cl-ctrl-active');
    localStorage.setItem('hub-people-muted', String(muted));
    btn.title = muted ? 'Unmute' : 'Mute';
    // When in voice, propagate to the LiveKit room so this is the canonical
    // mute control (Discord parity — bottom-bar mic is the always-visible
    // hardware mute, the call overlay no longer has its own button).
    if (getActiveVoiceSession()) {
      void overlayToggleMute().then(() => renderChannelList(container, callbacks));
    }
  });
  deafenBtn?.addEventListener('click', (e) => {
    const btn = e.currentTarget as HTMLElement;
    btn.classList.toggle('cl-ctrl-active');
    const deafened = btn.classList.contains('cl-ctrl-active');
    localStorage.setItem('hub-people-deafened', String(deafened));
    btn.title = deafened ? 'Undeafen' : 'Deafen';
    if (getActiveVoiceSession()) {
      void overlayToggleDeafen().then(() => renderChannelList(container, callbacks));
    }
  });

  // Discord-parity: chevron next to mic / headphone opens a per-input device
  // picker. Mic chevron \u2192 list of microphones (audioinput). Headphone chevron
  // \u2192 list of audio outputs (audiooutput). Selection persists in
  // localStorage and applies live to active call audio via setSinkId where
  // supported.
  document.getElementById('cl-mic-devices')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void openInputDevicePopover(e.currentTarget as HTMLElement, 'audioinput');
  });
  document.getElementById('cl-deafen-devices')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void openInputDevicePopover(e.currentTarget as HTMLElement, 'audiooutput');
  });

  // Search bar → fire a custom event so the global handler in app.ts opens
  // the quick switcher (keeps jump wiring in one place).
  document.getElementById('cl-search-btn')?.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('hp:open-quick-switcher'));
  });

  // Persistent call overlay wiring (A4)
  document.getElementById('cl-call-disconnect')?.addEventListener('click', () => {
    void overlayDisconnect().then(() => renderChannelList(container, callbacks));
  });
  document.getElementById('cl-call-settings')?.addEventListener('click', () => {
    // Voice settings panel is a future round — stay a no-op so the icon
    // looks complete without misleading the user with a half-built panel.
  });
  document.getElementById('cl-call-open')?.addEventListener('click', () => {
    const av = getActiveVoiceSession();
    if (av) callbacks.onSwitchChannel(`voice:${av.channelId}` as Channel);
  });

  // A4 — wire the activity meter to LiveKit speaker events. Subscribe once
  // per render; cancel on next render or whenever the channel-list is
  // re-mounted so we don't leak listeners across voice join/leave cycles.
  attachVoiceMeter();

  // A5 — quick voice action buttons (only present when in voice).
  document.getElementById('cl-vq-camera')?.addEventListener('click', () => {
    void overlayToggleCamera().then(() => renderChannelList(container, callbacks));
  });
  document.getElementById('cl-vq-screen')?.addEventListener('click', () => {
    void overlayToggleScreen().then(() => renderChannelList(container, callbacks));
  });

  // Mute/deafen are wired below in the user-bar handlers — they're the
  // always-visible hardware controls now (Discord parity, the call overlay
  // header no longer carries its own mute/deafen pair).

  // Server dropdown menu
  const headerEl = document.getElementById('cl-header-dropdown');
  if (headerEl && serverId) {
    headerEl.addEventListener('click', (e) => {
      const rect = headerEl.getBoundingClientRect();
      showServerDropdown(rect.left, rect.bottom + 4, serverId, callbacks);
      e.stopPropagation();
    });
  }
}

function showServerDropdown(x: number, y: number, serverId: string, callbacks: ChannelListCallbacks): void {
  const items: ContextMenuItem[] = [];

  if (callbacks.onServerSettings) {
    const cb = callbacks.onServerSettings;
    items.push({ label: 'Server Settings', icon: iconSettings(), action: () => cb(serverId) });
  }

  items.push({ label: 'Create Channel', icon: iconPlus(), action: () => showCreateChannelModal(serverId, callbacks, 'text') });
  items.push({ label: 'Create Category', icon: iconPlus(), action: () => showCreateCategoryModal(serverId, callbacks) });
  items.push({ label: 'Notification Settings', icon: iconBell(), action: () => { /* future */ } });

  if (callbacks.onManageMembers) {
    const cb = callbacks.onManageMembers;
    items.push({ label: 'Manage Members', icon: iconUserPlus(), action: () => cb(serverId) });
  }

  if (callbacks.onManageRoles) {
    const cb = callbacks.onManageRoles;
    items.push({ label: 'Manage Roles', icon: iconKey(), action: () => cb('roles') });
  }

  items.push({ label: 'Invite People', icon: iconUserPlus(), action: () => showInviteModal(serverId) });

  items.push({ label: '', divider: true });

  // Discord parity: server owner cannot leave their own server. They have
  // to either transfer ownership or delete the server outright (both
  // surfaced in Server Settings → Danger Zone). Backend enforces too —
  // the Leave button just gets hidden for the owner so they don't try.
  const myId = state.ownerId;
  const server = state.servers.find(s => s.id === serverId);
  const isOwner = !!myId && !!server && server.owner_id === myId;
  if (callbacks.onLeaveServer && !isOwner) {
    const cb = callbacks.onLeaveServer;
    items.push({ label: 'Leave Server', icon: iconLogOut(), danger: true, action: () => cb(serverId) });
  }

  showContextMenu(x, y, items);
}

function managementGroup(label: string, key: string, channels: ManagementDef[], current: Channel, pendingApprovals: number): string {
  const isCollapsed = collapsedCategories.has(key);
  // Use a single chevron-down + CSS rotation so the caret animates smoothly
  // (Discord parity: 90° via transform with `transition: transform 150ms ease`).
  const chevron = `<span class="cl-cat-caret${isCollapsed ? ' cl-cat-caret-collapsed' : ''}">${iconChevronDown()}</span>`;

  let html = `<div class="cl-category" data-category="${key}">
    ${chevron}
    ${escHtml(label)}
  </div>`;

  if (!isCollapsed) {
    for (const ch of channels) {
      const badge = ch.name === 'approvals' && pendingApprovals > 0
        ? `<span class="cl-channel-badge">${pendingApprovals}</span>`
        : '';
      const isActive = current === ch.name;
      const isUnread = unreadChannels.has(ch.name);
      const unreadDot = isUnread && !isActive ? '<span class="cl-unread-dot"></span>' : '';
      html += `<div class="cl-channel ${isActive ? 'active' : ''} ${isUnread && !isActive ? 'unread' : ''}" data-channel="${escAttr(ch.name)}">
        ${unreadDot}
        <span class="cl-channel-icon">${ch.icon()}</span>
        <span class="cl-channel-name">${escHtml(ch.label)}</span>
        ${badge}
      </div>`;
    }
  }

  return html;
}

/** Parent category header for categories that have sub-categories.
 *  Click opens the board view in the content area showing all subs as cards. */
function parentCategoryHeader(label: string, key: string, fullCatName: string, serverId: string): string {
  const isCollapsed = collapsedCategories.has(key);
  const chevron = `<span class="cl-cat-caret${isCollapsed ? ' cl-cat-caret-collapsed' : ''}">${iconChevronDown()}</span>`;

  const guestSettings = isNamedGuestCategory(fullCatName)
    ? `<button class="cl-category-add cl-category-guest-settings" data-category-full="${escAttr(fullCatName)}" title="${escAttr(label)} guest settings" aria-label="${escAttr(label)} guest settings">${iconSettings()}</button>`
    : '';
  return `<div class="cl-category cl-category-parent" data-category="${escAttr(key)}" data-category-label="${escAttr(label)}" data-category-full="${escAttr(fullCatName)}" data-server="${escAttr(serverId)}" data-has-subs="true">
    ${chevron}
    <span class="cl-category-label">${escHtml(label)}</span>
    ${guestSettings}
    <button class="cl-category-add cl-add-text-channel" data-server="${escAttr(serverId)}" data-category="${escAttr(label)}" title="Create sub-category or channel in ${escAttr(label)}" aria-label="Create in ${escAttr(label)}">${iconPlus()}</button>
    <button class="cl-category-board-btn" data-category-full="${escAttr(fullCatName)}" title="Open board view" aria-label="Open board view">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
    </button>
  </div>`;
}

function textChannelGroup(label: string, key: string, channels: ServerChannel[], current: Channel, serverId: string, isSub: boolean = false, categoryPath?: string): string {
  const isCollapsed = collapsedCategories.has(key);
  const chevron = `<span class="cl-cat-caret${isCollapsed ? ' cl-cat-caret-collapsed' : ''}">${iconChevronDown()}</span>`;

  const fullPath = categoryPath || label;
  const subCls = isSub ? ' cl-category-sub' : '';
  const guestSettings = isNamedGuestCategory(fullPath)
    ? `<button class="cl-category-add cl-category-guest-settings" data-category-full="${escAttr(fullPath)}" title="${escAttr(label)} guest settings" aria-label="${escAttr(label)} guest settings">${iconSettings()}</button>`
    : '';
  let html = `<div class="cl-category${subCls}" data-category="${escAttr(key)}" data-category-label="${escAttr(label)}" data-category-full="${escAttr(fullPath)}">
    ${chevron}
    <span class="cl-category-label">${escHtml(label)}</span>
    ${guestSettings}
    <button class="cl-category-add cl-add-text-channel" data-server="${escAttr(serverId)}" data-category="${escAttr(fullPath)}" title="Create channel in ${escAttr(label)}" aria-label="Create channel in ${escAttr(label)}">${iconPlus()}</button>
  </div>`;

  if (isCollapsed) return html;

  if (channels.length === 0) {
    html += `<div class="cl-empty-msg" style="padding:8px 12px;font-size:11px;color:var(--fg-muted)">No channels yet. Click + to create one.</div>`;
    return html;
  }

  const dragOn = !!state.flags['hp_drag_reorder'];
  const dragAttr = dragOn ? ' draggable="true"' : '';
  const chanSubCls = isSub ? ' cl-channel-sub' : '';
  for (const ch of channels) {
    const isActive = current === ch.name;
    const isUnread = unreadChannels.has(ch.name);
    const unreadDot = isUnread && !isActive ? '<span class="cl-unread-dot"></span>' : '';
    html += `<div class="cl-channel${chanSubCls} ${isActive ? 'active' : ''} ${isUnread && !isActive ? 'unread' : ''}"${dragAttr} data-channel="${escAttr(ch.name)}" data-channel-id="${escAttr(ch.id)}" data-channel-cat="${escAttr(fullPath)}">
      ${unreadDot}
      <span class="cl-channel-icon">${iconHash()}</span>
      <span class="cl-channel-name">${escHtml(ch.name)}</span>
      <span class="cl-channel-actions" role="group" aria-label="Channel actions">
        <button class="cl-channel-action" data-channel-action="invite" data-channel-target="${escAttr(ch.id)}" title="Invite People" aria-label="Invite People">${iconUserPlus()}</button>
        <button class="cl-channel-action" data-channel-action="settings" data-channel-target="${escAttr(ch.id)}" data-channel-name="${escAttr(ch.name)}" title="Edit Channel" aria-label="Edit Channel">${iconSettings()}</button>
      </span>
    </div>`;
  }

  return html;
}

function voiceChannelGroup(label: string, channels: VoiceChannel[], current: Channel, serverId: string): string {
  const isCollapsed = collapsedCategories.has('voice');
  const chevron = `<span class="cl-cat-caret${isCollapsed ? ' cl-cat-caret-collapsed' : ''}">${iconChevronDown()}</span>`;

  let html = `<div class="cl-category" data-category="voice">
    ${chevron}
    <span class="cl-category-label">${escHtml(label)}</span>
    <button class="cl-category-add" id="cl-add-voice-channel" data-server="${escAttr(serverId)}" title="Create voice channel" aria-label="Create voice channel">${iconPlus()}</button>
  </div>`;

  if (isCollapsed) return html;

  if (channels.length === 0) {
    html += `<div class="cl-empty-msg" style="padding:6px 12px;font-size:11px;color:var(--fg-muted)">No voice channels yet.</div>`;
    return html;
  }

  for (const ch of channels) {
    const sentinel = `voice:${ch.id}`;
    const isActive = current === sentinel;
    const isConnected = state.activeVoiceChannelId === ch.id;
    const isAiVoice = ch.channel_kind === 'ai_voice';
    html += `<div class="cl-channel cl-voice-channel ${isActive ? 'active' : ''}${isAiVoice ? ' cl-ai-voice' : ''}" data-channel="${escAttr(sentinel)}">
      <span class="cl-channel-icon">${iconSpeaker()}</span>
      <span class="cl-channel-name">${escHtml(ch.name)}</span>
      ${isAiVoice ? '<span class="cl-ai-badge" title="AI agent" style="font-size:10px;background:var(--accent,#5865f2);color:#fff;padding:1px 4px;border-radius:3px;margin-left:4px">AI</span>' : ''}
      ${isConnected ? '<span class="cl-voice-live" title="Connected">&#9679;</span>' : ''}
    </div>`;

    // Show participants under every voice channel — Discord parity. Live
    // participant data is only available for the channel this client is in;
    // for other voice channels the backend exposes /api/voice-channels/:id/peers
    // (already wired into getVoiceParticipants when present). Each participant
    // gets a green "in-call" dot next to their name so the channel-list
    // doubles as a presence indicator.
    const parts = isConnected ? getVoiceParticipants(ch.id) : [];
    for (const p of parts) {
      const initial = (p.name || '?').charAt(0).toUpperCase();
      html += `<div class="cl-voice-participant" title="${escAttr(p.name)}">
        <span class="cl-voice-participant-avatar">${escHtml(initial)}</span>
        <span class="cl-voice-incall-dot" title="In voice" aria-label="In voice"></span>
        <span class="cl-voice-participant-name">${escHtml(p.name)}${p.isLocal ? ' (you)' : ''}</span>
        ${p.muted ? '<span class="cl-voice-participant-muted" title="Muted"><svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg></span>' : ''}
      </div>`;
    }
  }

  return html;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function escHtml(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

/** Tiny inline icons reused by the rich-presence subtitle (A1). 12px square,
 *  currentColor-stroked so the .cl-user-status-text muted color flows through.
 *  Kept local to channel-list to avoid widening the global icon registry for
 *  one-off SVGs only used by the user bar. */
function iconSubtitleSpeaker(): string {
  return '<svg class="cl-user-sub-icon" viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>';
}
function iconSubtitleHash(): string {
  return '<svg class="cl-user-sub-icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>';
}

function renderUserSubtitle(
  customStatus: string,
  customEmoji: string,
  activeVoice: { channelId: string; channelName: string; participantCount: number } | null,
  current: Channel,
  userStatus: string,
): string {
  if (customStatus || customEmoji) {
    return `${customEmoji ? escHtml(customEmoji) + ' ' : ''}${escHtml(customStatus)}`;
  }
  if (activeVoice) {
    return `${iconSubtitleSpeaker()}<span class="cl-user-sub-name">${escHtml(activeVoice.channelName)}</span>`;
  }
  // Current channel: voice sentinels render with the speaker icon, text
  // channels with the hash. Management channels (single keyword) and the
  // default 'general' just render the label as #channel for parity.
  if (typeof current === 'string' && current.startsWith('voice:')) {
    const vcId = current.slice(6);
    const vc = state.voiceChannels.find(v => v.id === vcId);
    if (vc) return `${iconSubtitleSpeaker()}<span class="cl-user-sub-name">${escHtml(vc.name)}</span>`;
  }
  if (typeof current === 'string' && current) {
    return `${iconSubtitleHash()}<span class="cl-user-sub-name">${escHtml(current)}</span>`;
  }
  return escHtml(userStatusLabel(userStatus));
}

function userStatusLabel(key: string): string {
  switch (key) {
    case 'online': return 'Online';
    case 'idle': return 'Idle';
    case 'dnd': return 'Do Not Disturb';
    case 'invisible': return 'Invisible';
    default: return key;
  }
}

interface StatusDef { key: string; label: string; icon: () => string }
function iconStatusOnline(): string {
  return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="6"/></svg>';
}
function iconStatusIdle(): string {
  return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M18 12a6 6 0 1 1-6-6 4 4 0 0 0 6 6z"/></svg>';
}
function iconStatusDnd(): string {
  return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="6"/><rect x="7" y="11" width="10" height="2" rx="1" fill="var(--surface-2,#fff)"/></svg>';
}
function iconStatusInvisible(): string {
  return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="5"/></svg>';
}
const STATUSES: StatusDef[] = [
  { key: 'online',    label: 'Online',         icon: iconStatusOnline },
  { key: 'idle',      label: 'Idle',           icon: iconStatusIdle },
  { key: 'dnd',       label: 'Do Not Disturb', icon: iconStatusDnd },
  { key: 'invisible', label: 'Invisible',      icon: iconStatusInvisible },
];

/* Custom status (Discord-parity): emoji + text + auto-expire. Persisted as
   JSON in localStorage. Backwards-compatible read of the legacy text-only
   key so v1 users don't lose their status on first load. */
interface CustomStatus { text: string; emoji: string; expires_at: string | null }
const CS_KEY = 'hub-people-custom-status-v2';
const CS_LEGACY = 'hub-people-custom-status';
const CLEAR_OPTS: { key: string; label: string; mins: number | null }[] = [
  { key: 'never',  label: "Don't clear",  mins: null },
  { key: '30m',    label: '30 minutes',   mins: 30 },
  { key: '1h',     label: '1 hour',       mins: 60 },
  { key: '4h',     label: '4 hours',      mins: 240 },
  { key: 'today',  label: 'Today',        mins: 0 /* end-of-day */ },
];

export function readCustomStatus(): CustomStatus {
  try {
    const raw = localStorage.getItem(CS_KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<CustomStatus>;
      const expires = v.expires_at ?? null;
      if (expires && Date.parse(expires) <= Date.now()) {
        localStorage.removeItem(CS_KEY);
        return { text: '', emoji: '', expires_at: null };
      }
      return { text: v.text ?? '', emoji: v.emoji ?? '', expires_at: expires };
    }
  } catch { /* fallthrough */ }
  // Legacy migration
  const legacy = localStorage.getItem(CS_LEGACY) ?? '';
  return { text: legacy, emoji: '', expires_at: null };
}

function writeCustomStatus(text: string, emoji: string, clearKey: string): void {
  if (!text && !emoji) {
    localStorage.removeItem(CS_KEY);
    localStorage.removeItem(CS_LEGACY);
    return;
  }
  let expires_at: string | null = null;
  const opt = CLEAR_OPTS.find(o => o.key === clearKey);
  if (opt && opt.mins !== null) {
    if (opt.mins > 0) {
      expires_at = new Date(Date.now() + opt.mins * 60_000).toISOString();
    } else {
      // 'today' = end of local day
      const eod = new Date();
      eod.setHours(23, 59, 59, 999);
      expires_at = eod.toISOString();
    }
  }
  localStorage.setItem(CS_KEY, JSON.stringify({ text, emoji, expires_at }));
  // Keep legacy key in sync for back-compat consumers
  if (text) localStorage.setItem(CS_LEGACY, text);
  else localStorage.removeItem(CS_LEGACY);
}

function showAccountFlyout(container: HTMLElement, callbacks: ChannelListCallbacks): void {
  document.querySelectorAll('.account-flyout, .account-flyout-backdrop').forEach(el => el.remove());

  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
  const userName = owner?.name || (state.ownerId ? `Member-${state.ownerId.substring(0, 6)}` : 'Member');
  const userInitial = userName.charAt(0).toUpperCase();
  const userId = owner?.id || '';
  const current = localStorage.getItem('hub-people-status') || 'online';
  // v2 schema: {text, emoji, expires_at?}. Auto-expire if past TTL. Falls back
  // to legacy text-only key for migration. Discord parity.
  const cs = readCustomStatus();
  const customStatus = cs.text;
  const customEmoji = cs.emoji;

  // 1g — surface server-management items only when applicable so the flyout
  // stays uncluttered for the default-server-only case.
  const ownedServers = userId ? state.servers.filter(s => s.owner_id === userId) : [];
  const memberOfServers = userId
    ? state.serverMembers.filter(m => m.person_id === userId).map(m => m.server_id)
    : [];
  const serverManagementHtml = ownedServers.length > 0
    ? `<div class="af-divider"></div>
       <div class="af-section-title">Servers you own</div>
       ${ownedServers.map(s => `
         <button class="af-item af-server-jump" data-server="${escAttr(s.id)}">
           <span class="af-icon">${iconSettings()}</span>
           <span class="af-label">${escHtml(s.name)} settings</span>
         </button>`).join('')}
       ${memberOfServers.length >= 2
         ? `<div class="af-hint">Switch with Alt+Shift+↑/↓</div>`
         : ''}`
    : '';

  const flyout = document.createElement('div');
  flyout.className = 'account-flyout';
  flyout.innerHTML = `
    <div class="af-header">
      <div class="af-avatar">${escHtml(userInitial)}</div>
      <div class="af-user">
        <div class="af-name">${escHtml(userName)}</div>
        <div class="af-handle">${userId ? '@' + escHtml(userId.slice(0, 8)) : ''}</div>
      </div>
    </div>
    <div class="af-divider"></div>
    <div class="af-section-title">Status</div>
    ${STATUSES.map(s => `
      <button class="af-item af-status-item ${s.key === current ? 'active' : ''}" data-status="${escAttr(s.key)}">
        <span class="af-status-icon af-status-${escAttr(s.key)}">${s.icon()}</span>
        <span class="af-label">${escHtml(s.label)}</span>
        ${s.key === current ? `<span class="af-check">${iconCheck()}</span>` : ''}
      </button>
    `).join('')}
    <div class="af-custom">
      <button type="button" class="af-custom-emoji" id="af-emoji" title="Pick emoji" aria-label="Pick status emoji">${customEmoji ? escHtml(customEmoji) : '<span class="af-custom-emoji-empty">&#9786;</span>'}</button>
      <input type="text" class="af-custom-input" id="af-custom" placeholder="Set a custom status..."
             value="${customStatus.replace(/"/g, '&quot;')}" maxlength="128">
      ${(customStatus || customEmoji) ? '<button class="af-clear-btn" id="af-clear" title="Clear custom status">' + iconX() + '</button>' : ''}
    </div>
    <select class="af-custom-clear" id="af-clear-select" title="Clear after">
      ${CLEAR_OPTS.map(o => `<option value="${escAttr(o.key)}">Clear after: ${escHtml(o.label)}</option>`).join('')}
    </select>
    <div class="af-divider"></div>
    <button class="af-item" id="af-copy-id" ${userId ? '' : 'disabled'}>
      <span class="af-icon">${iconCopy()}</span>
      <span class="af-label">Copy User ID</span>
    </button>
    <button class="af-item" id="af-edit-profile">
      <span class="af-icon">${iconEdit()}</span>
      <span class="af-label">Edit Profile</span>
    </button>
    ${serverManagementHtml}
    <div class="af-divider"></div>
    <button class="af-item af-danger" id="af-logout">
      <span class="af-icon">${iconLogOut()}</span>
      <span class="af-label">Log Out</span>
    </button>
  `;

  // Append to BODY so #channel-list overflow:hidden cannot clip it. Position
  // anchored to the .cl-user-bar's bounding rect — right of the bar, growing
  // upward, max-width 320px. On mobile (≤540px) flip to a centred modal sheet.
  document.body.appendChild(flyout);

  const userBar = container.querySelector('.cl-user-bar') as HTMLElement | null;
  const isMobile = window.innerWidth <= 540;
  if (isMobile) {
    flyout.classList.add('account-flyout-mobile');
    const backdrop = document.createElement('div');
    backdrop.className = 'account-flyout-backdrop';
    document.body.appendChild(backdrop);
    backdrop.addEventListener('click', () => { flyout.remove(); backdrop.remove(); });
  } else if (userBar) {
    const r = userBar.getBoundingClientRect();
    const FLYOUT_WIDTH = 300;
    const PADDING = 8;
    let left = r.right + PADDING;
    if (left + FLYOUT_WIDTH > window.innerWidth - PADDING) {
      // Not enough room to the right (e.g. rail collapsed) — fall back to ABOVE the bar.
      left = Math.max(PADDING, r.left);
    }
    flyout.style.position = 'fixed';
    flyout.style.left = `${left}px`;
    flyout.style.bottom = `${Math.max(PADDING, window.innerHeight - r.top + 4)}px`;
    flyout.style.width = `${FLYOUT_WIDTH}px`;
    flyout.style.maxWidth = '320px';
  }

  // Status option clicks — persist to localStorage AND backend so other
  // clients (and member lists) see the same status without a page refresh.
  // Backend errors are non-fatal; the local UI still reflects the choice.
  flyout.querySelectorAll<HTMLElement>('.af-status-item').forEach(el => {
    el.addEventListener('click', () => {
      const status = el.dataset['status'] ?? 'online';
      localStorage.setItem('hub-people-status', status);
      if (userId) {
        void post(`/api/people/${encodeURIComponent(userId)}/status`, { status })
          .catch(() => { /* presence is best-effort, don't toast */ });
      }
      flyout.remove();
      renderChannelList(container, callbacks);
    });
  });

  // Custom status input \u2014 Enter saves with the selected expiration. Emoji
  // is pre-selected via the af-emoji popover above.
  const customInput = document.getElementById('af-custom') as HTMLInputElement | null;
  const clearSelect = document.getElementById('af-clear-select') as HTMLSelectElement | null;
  let pendingEmoji = customEmoji;

  const saveCustom = (): void => {
    const v = (customInput?.value ?? '').trim();
    const clearKey = clearSelect?.value ?? 'never';
    writeCustomStatus(v, pendingEmoji, clearKey);
    flyout.remove();
    renderChannelList(container, callbacks);
  };

  customInput?.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      saveCustom();
    } else if (e.key === 'Escape') {
      flyout.remove();
    }
  });

  // Tiny inline emoji palette \u2014 8 common Discord status picks + clear tile.
  // Laid out 3\u00d73 (square) with full keyboard nav (arrow keys, Enter/Space,
  // Escape). Focus lands on the first option so the picker is usable without
  // a pointer. Repeating the trigger click toggles the popover closed.
  const emojiBtn = document.getElementById('af-emoji') as HTMLButtonElement | null;
  emojiBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    const existing = document.getElementById('af-emoji-pop');
    if (existing) { existing.remove(); return; }
    const palette = ['\u{1F4AC}', '\u{1F389}', '\u{1F4DA}', '\u{1F3AE}', '\u{1F375}', '\u{1F3A7}', '\u{1F4F1}', '\u{1F4BB}'];
    const pop = document.createElement('div');
    pop.id = 'af-emoji-pop';
    pop.className = 'af-emoji-pop';
    pop.setAttribute('role', 'menu');
    pop.setAttribute('aria-label', 'Pick status emoji');
    pop.innerHTML = palette.map(em => `<button type="button" role="menuitem" class="af-emoji-opt" data-em="${escAttr(em)}" aria-label="${escAttr(em)}">${escHtml(em)}</button>`).join('') +
      `<button type="button" role="menuitem" class="af-emoji-opt af-emoji-clear" data-em="" aria-label="No emoji" title="No emoji">${iconX()}</button>`;
    document.body.appendChild(pop);
    const r = emojiBtn.getBoundingClientRect();
    const GAP = 6;
    const PAD = 8;
    // Measure actual size then clamp inside viewport.
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    let left = r.left;
    if (left + pw > window.innerWidth - PAD) left = Math.max(PAD, window.innerWidth - PAD - pw);
    let bottom = window.innerHeight - r.top + GAP;
    if (bottom + ph > window.innerHeight - PAD) bottom = Math.max(PAD, window.innerHeight - PAD - ph);
    pop.style.position = 'fixed';
    pop.style.left = `${left}px`;
    pop.style.bottom = `${bottom}px`;
    const opts = Array.from(pop.querySelectorAll<HTMLButtonElement>('.af-emoji-opt'));
    const pick = (btn: HTMLButtonElement): void => {
      pendingEmoji = btn.dataset['em'] ?? '';
      emojiBtn.innerHTML = pendingEmoji ? escHtml(pendingEmoji) : '<span class="af-custom-emoji-empty">\u263A</span>';
      pop.remove();
      emojiBtn.focus();
    };
    opts.forEach((btn, i) => {
      btn.tabIndex = i === 0 ? 0 : -1;
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        pick(btn);
      });
      btn.addEventListener('keydown', (ev) => {
        const cols = 3;
        const total = opts.length;
        let next = i;
        if (ev.key === 'ArrowRight') next = (i + 1) % total;
        else if (ev.key === 'ArrowLeft') next = (i - 1 + total) % total;
        else if (ev.key === 'ArrowDown') next = Math.min(total - 1, i + cols);
        else if (ev.key === 'ArrowUp') next = Math.max(0, i - cols);
        else if (ev.key === 'Home') next = 0;
        else if (ev.key === 'End') next = total - 1;
        else return;
        ev.preventDefault();
        ev.stopPropagation();
        opts[i]!.tabIndex = -1;
        opts[next]!.tabIndex = 0;
        opts[next]!.focus();
      });
    });
    opts[0]?.focus();
    // Escape closes the popover only (not the parent flyout). Outside click
    // dismiss stays as well.
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        pop.remove();
        document.removeEventListener('keydown', onKey, true);
        emojiBtn.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => {
      const off = (ev: MouseEvent): void => {
        if (!pop.contains(ev.target as Node)) {
          pop.remove();
          document.removeEventListener('click', off);
          document.removeEventListener('keydown', onKey, true);
        }
      };
      document.addEventListener('click', off);
    }, 0);
  });

  document.getElementById('af-clear')?.addEventListener('click', () => {
    writeCustomStatus('', '', 'never');
    flyout.remove();
    renderChannelList(container, callbacks);
  });

  document.getElementById('af-copy-id')?.addEventListener('click', () => {
    if (!userId) return;
    void navigator.clipboard.writeText(userId).then(() => showToast('User ID copied', 'ok'));
    flyout.remove();
  });

  document.getElementById('af-edit-profile')?.addEventListener('click', () => {
    flyout.remove();
    showEditProfileModal(container, callbacks);
  });

  document.getElementById('af-logout')?.addEventListener('click', () => {
    confirmModal('Log out', 'Log out?', () => {
      document.cookie = 'hub_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      localStorage.clear();
      window.location.reload();
    }, { confirmLabel: 'Log Out', variant: 'danger' });
  });

  // Server-jump items (1g) — switch to the server, then open its settings.
  flyout.querySelectorAll<HTMLElement>('.af-server-jump').forEach(btn => {
    btn.addEventListener('click', () => {
      const sid = btn.dataset['server'];
      if (!sid) return;
      flyout.remove();
      document.querySelector('.account-flyout-backdrop')?.remove();
      if (callbacks.onServerSettings) callbacks.onServerSettings(sid);
    });
  });

  // Close on Esc, scroll, or outside click. Backdrop handles mobile.
  const cleanup = () => {
    flyout.remove();
    document.querySelector('.account-flyout-backdrop')?.remove();
    document.removeEventListener('click', close);
    document.removeEventListener('keydown', escClose);
    window.removeEventListener('scroll', cleanup, true);
    window.removeEventListener('resize', cleanup);
  };
  const close = (e: MouseEvent) => {
    if (!flyout.contains(e.target as Node)) cleanup();
  };
  const escClose = (e: KeyboardEvent) => { if (e.key === 'Escape') cleanup(); };
  setTimeout(() => {
    document.addEventListener('click', close);
    document.addEventListener('keydown', escClose);
    window.addEventListener('scroll', cleanup, true);
    window.addEventListener('resize', cleanup);
  }, 0);
}

function showEditProfileModal(container: HTMLElement, callbacks: ChannelListCallbacks): void {
  const owner = state.ownerId ? state.people.find(p => p.id === state.ownerId) : null;
  if (!owner) {
    showToast('No profile to edit', 'err');
    return;
  }
  const pronouns = localStorage.getItem('hub-people-pronouns') || '';

  showModal(modalFrame('Edit Profile',
    `<div class="form-group">
       <label class="form-label">Display Name</label>
       <input class="form-input" id="ep-name" value="${escAttr(owner.name)}" maxlength="80" autocomplete="off">
     </div>
     <div class="form-group">
       <label class="form-label">Email <span style="color:var(--text-muted);font-weight:400">(read-only)</span></label>
       <input class="form-input" id="ep-email" value="${escAttr(owner.email || '')}" readonly style="opacity:0.7;cursor:not-allowed">
     </div>
     <div class="form-group">
       <label class="form-label">Pronouns</label>
       <input class="form-input" id="ep-pronouns" value="${escAttr(pronouns)}" maxlength="32" placeholder="they / them" autocomplete="off">
     </div>
     <div class="form-group">
       <label class="form-label">Bio</label>
       <textarea class="form-input" id="ep-bio" rows="4" maxlength="600" placeholder="Tell people a little about yourself.">${escHtml(owner.notes || '')}</textarea>
     </div>
     <div class="form-group">
       <label class="form-label">Avatar URL</label>
       <input class="form-input" id="ep-avatar" value="${escAttr(owner.avatar || '')}" placeholder="https://..." autocomplete="off">
       <div class="form-hint" style="font-size:11px;color:var(--text-muted);margin-top:4px">Paste an image URL. File upload coming later.</div>
     </div>`,
    `<button class="btn btn-secondary" id="ep-cancel">Cancel</button>
     <button class="btn btn-primary" id="ep-save">Save Profile</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('ep-cancel')?.addEventListener('click', closeModal);

  const saveBtn = document.getElementById('ep-save') as HTMLButtonElement | null;
  saveBtn?.addEventListener('click', () => {
    const nameEl = document.getElementById('ep-name') as HTMLInputElement | null;
    const bioEl = document.getElementById('ep-bio') as HTMLTextAreaElement | null;
    const avatarEl = document.getElementById('ep-avatar') as HTMLInputElement | null;
    const pronounsEl = document.getElementById('ep-pronouns') as HTMLInputElement | null;
    const newName = (nameEl?.value || '').trim();
    if (!newName) { showToast('Name cannot be empty', 'err'); nameEl?.focus(); return; }
    if (saveBtn.disabled) return;
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';

    if (pronounsEl) {
      const p = pronounsEl.value.trim();
      if (p) localStorage.setItem('hub-people-pronouns', p);
      else localStorage.removeItem('hub-people-pronouns');
    }

    void post<unknown>('/api/people/update', {
      id: owner.id,
      name: newName,
      avatar: avatarEl?.value.trim() || '',
      notes: bioEl?.value || '',
    }).then((updated) => {
      const u = updated as { id: string; name: string; avatar: string; notes: string };
      const idx = state.people.findIndex(p => p.id === owner.id);
      if (idx >= 0) {
        const cur = state.people[idx];
        if (cur) {
          state.people[idx] = { ...cur, name: u.name, avatar: u.avatar, notes: u.notes };
        }
      }
      closeModal();
      showToast('Profile saved', 'ok');
      renderChannelList(container, callbacks);
    }).catch(e => {
      showToast(String(e), 'err');
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save Profile'; }
    });
  });
}

function showInviteModal(serverId: string): void {
  showModal(modalFrame('Invite People',
    `<p style="color:var(--fg-muted);font-size:14px;margin-bottom:12px">Generate an invite link to share with others.</p>
     <div class="form-group">
       <label class="form-label">Expires After</label>
       <select class="form-input" id="inv-expires">
         <option value="86400">24 hours</option>
         <option value="604800" selected>7 days</option>
         <option value="2592000">30 days</option>
         <option value="">Never</option>
       </select>
     </div>
     <div class="form-group">
       <label class="form-label">Max Uses</label>
       <select class="form-input" id="inv-uses">
         <option value="">No limit</option>
         <option value="1">1 use</option>
         <option value="5">5 uses</option>
         <option value="10">10 uses</option>
         <option value="25">25 uses</option>
       </select>
     </div>
     <div class="form-group">
       <label class="form-label">Custom URL <span style="color:var(--fg-muted);font-weight:normal;font-size:12px">(optional, free)</span></label>
       <input class="form-input" id="inv-vanity" placeholder="my-team" maxlength="32" pattern="[a-z0-9-]{3,32}" autocomplete="off" style="font-family:monospace">
       <div style="font-size:11px;color:var(--fg-muted);margin-top:4px">3\u201332 chars: lowercase letters, digits, hyphens. Leave blank for a random code.</div>
     </div>
     <div id="inv-result" class="inv-result-banner" style="display:none">
       <div class="form-group" style="margin-bottom:0">
         <label class="form-label">Invite Link</label>
         <div style="display:flex;gap:8px">
           <input class="form-input" id="inv-link" readonly style="font-family:monospace">
           <button class="btn btn-primary" id="inv-copy">Copy</button>
         </div>
       </div>
     </div>`,
    `<button class="btn btn-secondary" id="inv-cancel">Cancel</button>
     <button class="btn btn-primary" id="inv-generate">Generate Invite</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('inv-cancel')?.addEventListener('click', closeModal);

  document.getElementById('inv-generate')?.addEventListener('click', () => {
    const expiresVal = (document.getElementById('inv-expires') as HTMLSelectElement)?.value;
    const maxUsesVal = (document.getElementById('inv-uses') as HTMLSelectElement)?.value;
    const vanityVal = ((document.getElementById('inv-vanity') as HTMLInputElement | null)?.value || '').trim().toLowerCase();
    const myId = state.ownerId || state.people[0]?.id || '';

    void post<{ code: string }>('/api/invites/create', {
      server_id: serverId,
      created_by: myId,
      expires_hours: expiresVal ? Math.round(parseInt(expiresVal) / 3600) : null,
      max_uses: maxUsesVal ? parseInt(maxUsesVal) : null,
      ...(vanityVal ? { vanity: vanityVal } : {}),
    }).then(data => {
      const code = (data as { code: string }).code || String(data);
      const link = `${window.location.origin}/people/#invite/${code}`;
      const resultEl = document.getElementById('inv-result');
      const linkInput = document.getElementById('inv-link') as HTMLInputElement;
      if (resultEl) resultEl.style.display = 'block';
      if (linkInput) linkInput.value = link;

      document.getElementById('inv-copy')?.addEventListener('click', () => {
        void navigator.clipboard.writeText(link);
        showToast('Link copied', 'ok');
      });
    }).catch(e => showToast(String(e), 'err'));
  });
}

/** localStorage-backed list of empty categories per server. Categories with
 *  channels are derived from server_channels.category at render time; this
 *  set holds categories the user created via "Create Category" that don't
 *  have any channels yet so the empty group still shows up in the list.
 *  When a channel is added to one of these categories, it stays in the set
 *  (cheap, no churn). When the user removes the last channel from a custom
 *  category, the empty stub stays until they explicitly delete it. */
function emptyCategoriesKey(serverId: string): string {
  return `hub-people-empty-cats:${serverId}`;
}
export function getEmptyCategories(serverId: string): string[] {
  try {
    const raw = localStorage.getItem(emptyCategoriesKey(serverId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : [];
  } catch { return []; }
}
function addEmptyCategory(serverId: string, name: string): void {
  const cats = getEmptyCategories(serverId);
  if (!cats.includes(name)) cats.push(name);
  localStorage.setItem(emptyCategoriesKey(serverId), JSON.stringify(cats));
}

export function applyCreatedTextChannel(ch: ServerChannel): void {
  pendingCreatedTextChannelIds.add(ch.id);
  state.channels = state.channels.filter(c => c.id !== ch.id);
  state.channels.push(ch);
}

export function applyCreatedVoiceChannel(ch: VoiceChannel): void {
  pendingCreatedVoiceChannelIds.add(ch.id);
  state.voiceChannels = state.voiceChannels.filter(v => v.id !== ch.id);
  state.voiceChannels.push(ch);
}

function renderChannelListNow(callbacks: ChannelListCallbacks): void {
  const channelList = document.getElementById('channel-list');
  if (channelList) renderChannelList(channelList, callbacks);
}

function notifyChannelsChanged(callbacks: ChannelListCallbacks): void {
  renderChannelListNow(callbacks);
  if (callbacks.onChannelsChanged) callbacks.onChannelsChanged();
}

function showCreateCategoryModal(serverId: string, callbacks: ChannelListCallbacks): void {
  showModal(modalFrame('Create Category',
    `<div class="form-group">
      <label class="form-label" for="cat-name">Category Name</label>
      <input class="form-input" id="cat-name" placeholder="My Category" maxlength="60" autocomplete="off">
      <div class="form-hint">Categories group related channels. You can add channels to it after creation by clicking the + next to its header.</div>
    </div>`,
    `<button class="btn btn-secondary" id="cat-cancel">Cancel</button>
     <button class="btn btn-primary" id="cat-create">Create Category</button>`
  ));
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('cat-cancel')?.addEventListener('click', closeModal);
  const input = document.getElementById('cat-name') as HTMLInputElement | null;
  input?.focus();
  document.getElementById('cat-create')?.addEventListener('click', () => {
    const name = (input?.value || '').trim();
    if (!name) { input?.focus(); return; }
    addEmptyCategory(serverId, name);
    closeModal();
    showToast(`Category "${name}" created`, 'ok');
    notifyChannelsChanged(callbacks);
  });
}

function showCreateSubCategoryModal(serverId: string, parentCategory: string, callbacks: ChannelListCallbacks): void {
  showModal(modalFrame('Create Sub-Category',
    `<div class="form-group">
      <label class="form-label" for="subcat-name">Sub-Category Name</label>
      <input class="form-input" id="subcat-name" placeholder="My Notes" maxlength="60" autocomplete="off">
      <div class="form-hint">Creates a sub-category under <strong>${escHtml(parentCategory)}</strong>. Channels added to it will appear nested.</div>
    </div>`,
    `<button class="btn btn-secondary" id="subcat-cancel">Cancel</button>
     <button class="btn btn-primary" id="subcat-create">Create</button>`
  ));
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('subcat-cancel')?.addEventListener('click', closeModal);
  const input = document.getElementById('subcat-name') as HTMLInputElement | null;
  input?.focus();
  document.getElementById('subcat-create')?.addEventListener('click', () => {
    const name = (input?.value || '').trim();
    if (!name) { input?.focus(); return; }
    const fullName = `${parentCategory}/${name}`;
    addEmptyCategory(serverId, fullName);
    closeModal();
    showToast(`Sub-category "${name}" created under ${parentCategory}`, 'ok');
    notifyChannelsChanged(callbacks);
  });
}

function showCreateChannelModal(serverId: string, callbacks: ChannelListCallbacks, initialType: 'text' | 'voice' = 'text', presetCategory: string = 'Text Channels'): void {
  showModal(modalFrame('Create Channel',
    `<div class="form-group">
      <label class="form-label">Channel Type</label>
      <div class="cc-type-grid">
        <button type="button" class="cc-type-card ${initialType === 'text' ? 'active' : ''}" data-type="text">
          <span class="cc-type-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M5.88 21l1.54-7H2.5l.34-2h4.92l1.12-5H3.96l.34-2h4.92L10.76 3h2l-1.54 7h5L17.76 3h2l-1.54 7H23.14l-.34 2h-4.92l-1.12 5h4.92l-.34 2h-4.92L14.88 21h-2l1.54-7h-5L7.88 21h-2zm4.92-9h5l1.12-5h-5L10.8 12z"/></svg>
          </span>
          <span class="cc-type-body">
            <strong>Text</strong>
            <span class="cc-type-sub">Send messages, images, GIFs, and files.</span>
          </span>
          <span class="cc-type-radio" aria-hidden="true"></span>
        </button>
        <button type="button" class="cc-type-card ${initialType === 'voice' ? 'active' : ''}" data-type="voice">
          <span class="cc-type-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>
          </span>
          <span class="cc-type-body">
            <strong>Voice</strong>
            <span class="cc-type-sub">Hop in to talk with video, screen share, and activities.</span>
          </span>
          <span class="cc-type-radio" aria-hidden="true"></span>
        </button>
      </div>
    </div>
    <div class="form-group">
      <label class="form-label" for="cc-name">Channel Name</label>
      <input class="form-input" id="cc-name" placeholder="new-channel" maxlength="100" autocomplete="off">
      <div class="form-hint">Lowercase, no spaces. Hyphens allowed.</div>
    </div>
    <div class="form-group">
      <div id="cc-preview" class="cc-preview">Channel preview: <strong id="cc-preview-name">#new-channel</strong></div>
    </div>`,
    `<button class="btn btn-secondary" id="cc-cancel">Cancel</button>
     <button class="btn btn-primary" id="cc-create">Create Channel</button>`
  ));

  let channelType: 'text' | 'voice' = initialType;

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('cc-cancel')?.addEventListener('click', closeModal);

  const previewEl = document.getElementById('cc-preview-name');
  const nameInput = document.getElementById('cc-name') as HTMLInputElement | null;
  nameInput?.addEventListener('input', () => {
    const slug = (nameInput.value || 'new-channel').trim().toLowerCase().replace(/\s+/g, '-');
    if (previewEl) previewEl.textContent = `#${slug}`;
  });
  nameInput?.focus();

  document.querySelectorAll<HTMLButtonElement>('.cc-type-card').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.cc-type-card').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      channelType = (btn.dataset['type'] === 'voice') ? 'voice' : 'text';
    });
  });

  const createBtn = document.getElementById('cc-create') as HTMLButtonElement | null;
  createBtn?.addEventListener('click', () => {
    if (!nameInput) return;
    const name = nameInput.value.trim();
    if (!name) { nameInput.focus(); return; }
    if (createBtn.disabled) return;
    createBtn.disabled = true;
    createBtn.textContent = 'Creating…';

    if (channelType === 'voice') {
      void post<VoiceChannel>('/api/voice-channels', {
        server_id: serverId,
        name,
        created_by: state.ownerId ?? '',
      }).then(async (vc) => {
        applyCreatedVoiceChannel(vc);
        closeModal();
        showToast(`Voice channel ${vc.name} created`, 'ok');
        if (state.currentServer) await loadChannelsForServer(state.currentServer);
        notifyChannelsChanged(callbacks);
      }).catch(e => {
        errToast(e);
        if (createBtn) { createBtn.disabled = false; createBtn.textContent = 'Create Channel'; }
      });
      return;
    }

    void post<ServerChannel>('/api/channels/create', {
      server_id: serverId,
      name,
      channel_type: 'text',
      category: presetCategory,
    }).then(async (ch) => {
      applyCreatedTextChannel(ch);
      closeModal();
      showToast(`Channel #${ch.name} created`, 'ok');
      // Re-fetch so the new channel passes the (previously-cached) visibility
      // filter — otherwise it stays hidden until a server switch / refresh.
      if (state.currentServer) await loadChannelsForServer(state.currentServer);
      notifyChannelsChanged(callbacks);
    }).catch(e => {
      errToast(e);
      if (createBtn) { createBtn.disabled = false; createBtn.textContent = 'Create Channel'; }
    });
  });
}

/** Fetch server + voice channels into state for the given server. */
export async function loadChannelsForServer(serverId: string): Promise<void> {
  try {
    const chans = await get<ServerChannel[]>(`/api/channels?server_id=${encodeURIComponent(serverId)}`);
    for (const ch of chans) pendingCreatedTextChannelIds.delete(ch.id);
    const pendingChans = state.channels.filter(c =>
      c.server_id === serverId &&
      pendingCreatedTextChannelIds.has(c.id) &&
      !chans.some(fetched => fetched.id === c.id)
    );
    state.channels = [
      ...state.channels.filter(c => c.server_id !== serverId),
      ...chans,
      ...pendingChans,
    ];
  } catch {
    state.channels = state.channels.filter(c => c.server_id !== serverId || pendingCreatedTextChannelIds.has(c.id));
  }
  try {
    const voice = await get<VoiceChannel[]>(`/api/voice-channels?server_id=${encodeURIComponent(serverId)}`);
    for (const vc of voice) pendingCreatedVoiceChannelIds.delete(vc.id);
    const pendingVoice = state.voiceChannels.filter(v =>
      v.server_id === serverId &&
      pendingCreatedVoiceChannelIds.has(v.id) &&
      !voice.some(fetched => fetched.id === v.id)
    );
    state.voiceChannels = [
      ...state.voiceChannels.filter(v => v.server_id !== serverId),
      ...voice,
      ...pendingVoice,
    ];
  } catch {
    state.voiceChannels = state.voiceChannels.filter(v => v.server_id !== serverId || pendingCreatedVoiceChannelIds.has(v.id));
  }
  // Role colors for message-author coloring (Discord parity — top role wins).
  try {
    const colors = await get<Record<string, string>>(
      `/api/server_members/role_colors?server_id=${encodeURIComponent(serverId)}`,
    );
    state.roleColors = colors ?? {};
  } catch {
    state.roleColors = {};
  }
  // Populate visible-channel filter for the sidebar. If we can't fetch it, fall
  // back to null (show everything) rather than hiding channels on a flaky API.
  try {
    const pid = state.ownerId ?? '';
    const visible = await get<string[]>(
      `/api/channel-permissions/visible?server_id=${encodeURIComponent(serverId)}&person_id=${encodeURIComponent(pid)}`,
    );
    state.visibleChannelNames = new Set(visible);
  } catch {
    state.visibleChannelNames = null;
  }
}

/** Discord-parity device picker for the mic/headphone chevrons in the
 *  bottom-left user-bar. Anchored above the chevron button. Lists every
 *  device of the requested kind from navigator.mediaDevices. Saves
 *  selection to localStorage and (for outputs) applies setSinkId
 *  immediately to every active call audio element so headset swaps
 *  during a call work without leaving + rejoining. */
async function openInputDevicePopover(anchor: HTMLElement, kind: 'audioinput' | 'audiooutput' | 'videoinput'): Promise<void> {
  document.querySelectorAll('.cl-dev-pop').forEach(el => el.remove());

  const lsKey = kind === 'audioinput' ? 'hub-people-voice-mic'
              : kind === 'audiooutput' ? 'hub-people-voice-out'
              : 'hub-people-voice-cam';
  const saved = localStorage.getItem(lsKey) || '';

  let devices: MediaDeviceInfo[] = [];
  try {
    // First-time enumerate often returns blank labels; one-shot getUserMedia
    // with the matching kind grants permission so labels populate.
    try {
      if (kind === 'audioinput') await navigator.mediaDevices.getUserMedia({ audio: true });
      else if (kind === 'videoinput') await navigator.mediaDevices.getUserMedia({ video: true });
      // audiooutput needs no permission \u2014 enumerate works directly
    } catch { /* user may have denied; still enumerate */ }
    const devs = await navigator.mediaDevices.enumerateDevices();
    devices = devs.filter(d => d.kind === kind);
  } catch { /* unsupported \u2014 popover will show fallback msg */ }

  const titleByKind: Record<typeof kind, string> = {
    audioinput: 'Microphone',
    audiooutput: 'Audio Output',
    videoinput: 'Camera',
  };

  const pop = document.createElement('div');
  pop.className = 'cl-dev-pop';
  pop.setAttribute('role', 'menu');
  pop.setAttribute('aria-label', `Select ${titleByKind[kind]}`);
  pop.innerHTML = `
    <div class="cl-dev-pop-title">${escHtml(titleByKind[kind])}</div>
    ${devices.length === 0
      ? `<div class="cl-dev-pop-empty">No ${escHtml(titleByKind[kind].toLowerCase())} detected.</div>`
      : devices.map(d => {
          const checked = d.deviceId === saved || (!saved && devices[0]?.deviceId === d.deviceId);
          const label = d.label || `Device ${d.deviceId.slice(0, 8)}`;
          return `<button type="button" class="cl-dev-pop-item${checked ? ' selected' : ''}" data-id="${escAttr(d.deviceId)}" role="menuitemradio" aria-checked="${checked}">
            <span class="cl-dev-pop-check">${checked ? '\u2713' : ''}</span>
            <span class="cl-dev-pop-label">${escHtml(label)}</span>
          </button>`;
        }).join('')}
  `;
  document.body.appendChild(pop);

  // Anchor: above the chevron, right-aligned to the user-bar.
  const r = anchor.getBoundingClientRect();
  pop.style.position = 'fixed';
  pop.style.bottom = `${Math.max(8, window.innerHeight - r.top + 6)}px`;
  pop.style.right = `${Math.max(8, window.innerWidth - r.right + 0)}px`;

  const close = (): void => {
    pop.remove();
    document.removeEventListener('click', off);
    document.removeEventListener('keydown', onKey);
  };
  const off = (e: MouseEvent): void => { if (!pop.contains(e.target as Node)) close(); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  setTimeout(() => document.addEventListener('click', off), 0);
  document.addEventListener('keydown', onKey);

  pop.querySelectorAll<HTMLButtonElement>('.cl-dev-pop-item').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset['id'] ?? '';
      localStorage.setItem(lsKey, id);
      // Live-apply against the current LiveKit room so a device swap during
      // a call doesn't require leaving + rejoining (Discord parity). Each
      // applyInput/applyOutput call no-ops cleanly when not in voice.
      if (kind === 'audioinput') {
        await applyInputDevice(id);
      } else if (kind === 'audiooutput') {
        await applyOutputDevice(id);
        // Belt-and-braces: also setSinkId on every <audio> element directly
        // so non-LiveKit audio (toasts, future media) follows the choice.
        const audios = document.querySelectorAll<HTMLAudioElement>('audio[data-call-identity]');
        for (const a of audios) {
          try {
            const setSink = (a as unknown as { setSinkId?: (id: string) => Promise<void> }).setSinkId;
            if (typeof setSink === 'function') await setSink.call(a, id);
          } catch { /* setSinkId requires HTTPS + permission on some browsers */ }
        }
      }
      close();
    });
  });
}
